import React, { Component, Fragment } from 'react';
import fp from 'lodash/fp';
import { DragDrop } from '@uppy/react';
import {
  Button,
  FormControl,
  Form,
  Icon,
  TagPicker,
  Progress,
  IconButton,
  SelectPicker,
  InputNumber,
  Loader,
  ButtonToolbar,
  HelpBlock,
  Message
} from 'rsuite';
import { PackageType } from '../../../constants/inventory/InventoryTypes';
import { parseInt } from '../../../helpers/utils';
import uppyAwsS3Upload from '../../../helpers/uppyAwsS3Upload';
import { AppConfig } from '../../../helpers/config';
import {
  defualtFormValue,
  defaultPackageItemValue,
  createModelSchema,
  FormPropTypes,
  FormDefaultProps
} from './settings';
import InventoryImage from '../Image';
import PackageItemsList from '../PackageItemsList';
import { ControlLabel, FormGroup } from '../../widgets/FormElements';
import HealthLevelOptions from '../../../constants/common/HealthLevelOptions';
import {
  FileTag,
  ImageWrapper,
  RemoveImageIcon,
  QuantityInput
} from './elements';

const { Line: ProgressLine } = Progress;

export default class InventoryItemForm extends Component {
  static propTypes = FormPropTypes;

  static defaultProps = FormDefaultProps;

  constructor(props) {
    super(props);
    this.state = {
      formValue: defualtFormValue,
      formError: {},
      uploadFile: null,
      uploadProgress: null,
      packageItemValue: defaultPackageItemValue
    };
    this.formRef = React.createRef();

    this.uppyUpload = uppyAwsS3Upload({
      type: 'image',
      bucket: fp.get('buckets.inventory', AppConfig),
      onFileAdded: ({ data: { name } }) => {
        this.setState({ uploadFile: name });
      },
      onUpload: () => {
        this.setState({ uploadProgress: true });
      },
      onUploadProgress: (_, { bytesUploaded, bytesTotal }) => {
        const uploadProgress = (100 * bytesUploaded) / bytesTotal;
        this.setState({ uploadProgress });
      },
      onUploadError: () => {
        this.resetFileUpload();
      },
      onComplete: result => {
        const { formValue: currentFormValue } = this.state;
        const photoUrl = fp.get('successful.0.uploadURL', result);
        const photo = fp.last(photoUrl.split('/'));
        const formValue = fp.set('photo', photo, currentFormValue);
        this.setState({ formValue });
        this.handleSubmit();
      }
    });
  }

  static getDerivedStateFromProps(props, state) {
    const { item, packageItems, isFetchedPackageItems } = props;
    // Update formValue
    if (item && fp.isEqual(state.formValue, defualtFormValue)) {
      return {
        ...state,
        formValue: {
          ...item,
          weight: parseFloat(item.weight),
          tags: fp.map('name', item.tags)
        }
      };
    }

    const isLocationIdSet = fp.get('formValue.locationId', state);
    const hasPackageItems = isFetchedPackageItems && fp.size(packageItems) > 0;
    if (item && hasPackageItems && !isLocationIdSet) {
      const locationId = fp.get('0.location.id', packageItems);
      return fp.set('formValue.locationId', locationId, state);
    }

    return state;
  }

  componentDidMount() {
    const { onInit, item, type } = this.props;
    onInit(item, type);
  }

  componentWillUnmount() {
    this.uppyUpload.close();
  }

  get isPackage() {
    const { type } = this.props;
    return PackageType === type;
  }

  // Convert option value to label
  get customTagOption() {
    const { tagOptions } = this.props;
    return fp.map(({ label }) => ({ value: label, label }))(tagOptions);
  }

  get isUploading() {
    const { uploadFile, uploadProgress } = this.state;
    return uploadFile && uploadProgress && uploadProgress < 100;
  }

  get savingInProgress() {
    const { uploadProgress } = this.state;
    const { isSaving, isSaved } = this.props;
    return this.isUploading || (uploadProgress === 100 && !isSaved) || isSaving;
  }

  get locationZonesOptions() {
    const { formValue } = this.state;
    const { zonesByLocationOptions } = this.props;
    return fp.getOr([], formValue.locationId, zonesByLocationOptions);
  }

  setFormDefaultEmptyFields = formValue => {
    const { price, weight, points, minCount, maxCount } = formValue;

    return {
      ...formValue,
      price: price || '0.00',
      weight: weight || 0,
      points: points || 0,
      minCount: minCount || 0,
      maxCount: maxCount || 0
    };
  };

  handleFormChange = formValue => {
    this.setState({ formValue });
  };

  handleFormCheck = error => {
    this.setState({ formError: error });
  };

  handleSubmit = () => {
    if (!this.formRef.current.check()) {
      return;
    }

    const { uploadFile, uploadProgress } = this.state;
    if (uploadFile && uploadProgress === null) {
      this.uppyUpload.upload();
      return;
    }

    const { formValue } = this.state;
    const { onSubmit, packageItems } = this.props;
    const formData = this.setFormDefaultEmptyFields(formValue);

    if (this.isPackage) {
      onSubmit(formData, packageItems);
    } else {
      onSubmit(formData);
    }
  };

  resetFileUpload = () => {
    this.uppyUpload.reset();
    this.setState({ uploadFile: null, uploadProgress: null });
  };

  handleReset = () => {
    const { onResetPackageItems } = this.props;
    this.setState({ formValue: defualtFormValue });
    this.resetFileUpload();
    onResetPackageItems();
  };

  handleRemovePhoto = () => {
    const { formValue } = this.state;
    this.setState({ formValue: fp.set('photo', null, formValue) });
  };

  getPackageItemOptionValue = (key, value) => {
    const { packageItemValue } = this.state;
    const { itemOptions, locationOptions } = this.props;
    const optionsByProp = {
      item: itemOptions,
      location: locationOptions,
      zone: this.locationZonesOptions
    };
    const options = fp.get(key, optionsByProp);
    const optionVal = fp.find({ value }, options);
    const name = fp.getOr(null, 'label', optionVal);
    return fp.set(key, { id: value, name }, packageItemValue);
  };

  updatePackageItemValue = (key, value) => {
    const { packageItemValue } = this.state;
    const { itemOptions, locationOptions } = this.props;
    const optionsByProp = {
      item: itemOptions,
      location: locationOptions,
      zone: this.locationZonesOptions
    };
    // dropdown input
    if (fp.has(key, optionsByProp)) {
      const options = fp.get(key, optionsByProp);
      const optionVal = fp.find({ value }, options);
      const name = fp.getOr(null, 'label', optionVal);
      return fp.set(key, { id: value, name }, packageItemValue);
    }
    return fp.set(key, value, packageItemValue);
  };

  handlePackageItemChange = value => {
    const packageItemValue = this.updatePackageItemValue('item', value);
    this.setState({ packageItemValue });
  };

  handlePackageItemZoneChange = value => {
    const { formValue } = this.state;
    const { locationId } = formValue;
    const { location } = this.updatePackageItemValue('location', locationId);
    const packageItemValue = {
      ...this.updatePackageItemValue('zone', value),
      location
    };
    this.setState({ packageItemValue });
  };

  handlePackageItemQtyChange = value => {
    const quantity = value ? fp.parseInt(10, value) : '';
    const packageItemValue = this.updatePackageItemValue('quantity', quantity);
    this.setState({ packageItemValue });
  };

  resetPackageItemInputs = () => {
    this.setState({ packageItemValue: defaultPackageItemValue });
  };

  handleAddPackageItem = () => {
    const { onAddPackageItem } = this.props;
    const { packageItemValue } = this.state;
    onAddPackageItem(packageItemValue);

    const { item, quantity } = packageItemValue;
    this.updateFormValueComputedProps(item, quantity);

    this.resetPackageItemInputs();
  };

  handleIncrementPackageItem = packageItem => {
    const { onIncrementQtyPackageItem } = this.props;
    onIncrementQtyPackageItem(packageItem);
    this.updateFormValueComputedProps(
      packageItem.item,
      packageItem.quantity + 1
    );
  };

  handleDecrementPackageItem = packageItem => {
    const { onDecrementQtyPackageItem } = this.props;
    onDecrementQtyPackageItem(packageItem);
    this.updateFormValueComputedProps(
      packageItem.item,
      packageItem.quantity - 1
    );
  };

  handleUndoDeletePackageItem = packageItem => {
    const { onUndoDeletePackageItem } = this.props;
    onUndoDeletePackageItem(packageItem);
    const { quantity } = packageItem;
    this.updateFormValueComputedProps(packageItem.item, quantity);
  };

  handleDeletePackageItem = packageItem => {
    const { onDeletePackageItem } = this.props;
    onDeletePackageItem(packageItem);
    this.updateFormValueComputedProps(packageItem.item, 0);
  };

  handleMinCountOnChange = value => {
    const { formValue } = this.state;
    const maxCount = parseInt(formValue.maxCount);
    const minCount = parseInt(value);
    if (maxCount >= minCount) {
      this.formRef.current.cleanErrorForFiled('maxCount', () => {
        this.formRef.current.check();
      });
    }
  };

  updateFormValueComputedProps = (item, quantity) => {
    const { formValue } = this.state;
    const { itemOptions, packageItems } = this.props;
    const itemsById = fp.keyBy('value', itemOptions);
    const itemMeta = fp.get(`${item.id}.meta`, itemsById);
    const computedValues = {
      price: quantity * parseFloat(itemMeta.price),
      weight: quantity * parseFloat(itemMeta.weight),
      points: quantity * parseFloat(itemMeta.points)
    };
    const sumComputedValues = fp.reduce(
      (sum, each) => {
        const id = fp.get('item.id', each);
        if (id === item.id || each.deleted) return sum;

        const meta = fp.get(`${id}.meta`, itemsById);
        const price = each.quantity * parseFloat(meta.price);
        const weight = each.quantity * parseFloat(meta.weight);
        const points = each.quantity * parseFloat(meta.points);
        return {
          price: sum.price + price,
          weight: sum.weight + weight,
          points: sum.points + points
        };
      },
      computedValues,
      packageItems
    );

    const newFormValue = {
      ...formValue,
      price: sumComputedValues.price.toFixed(2),
      weight: sumComputedValues.weight.toFixed(4),
      points: sumComputedValues.points
    };
    this.setState({ formValue: newFormValue });
  };

  renderUploadProgress() {
    const { uploadProgress } = this.state;
    const status = uploadProgress < 100 ? 'active' : 'success';
    return (
      <ProgressLine
        percent={Math.round(uploadProgress)}
        status={status}
        style={{ width: 320, float: 'right', marginRight: 100 }}
      />
    );
  }

  renderPhoto() {
    const {
      uploadFile,
      formValue: { photo }
    } = this.state;
    if (uploadFile && !photo) {
      return (
        <FileTag onClick={this.resetFileUpload} closable>
          {uploadFile}
        </FileTag>
      );
    }

    return (
      photo && (
        <ImageWrapper>
          <RemoveImageIcon
            size="xs"
            icon={<Icon icon="close" />}
            onClick={this.handleRemovePhoto}
          />
          <InventoryImage
            image={photo}
            alt="photo"
            style={{ borderRadius: 5 }}
          />
        </ImageWrapper>
      )
    );
  }

  renderPackageItemsFormControls() {
    const {
      locationOptions,
      packageActiveReservationOrderItemsCount
    } = this.props;
    const { packageItemValue, formValue, formError } = this.state;
    const { itemOptions, packageItems, isFetchingPackageItems } = this.props;
    const existingItemsById = fp.keyBy(fp.get('item.id'), packageItems);
    const nonExistingItemOptions = fp.reject(
      ({ value }) => fp.has(value, existingItemsById),
      itemOptions
    );

    const allowEditPackageItems = packageActiveReservationOrderItemsCount < 1;
    const disabledAddBtn =
      !packageItemValue.item ||
      !packageItemValue.zone ||
      !packageItemValue.quantity;
    const disabledInput = !fp.isNumber(formValue.locationId);

    return (
      <Fragment>
        <FormGroup>
          <ControlLabel>Location *</ControlLabel>
          <FormControl
            name="locationId"
            accepter={SelectPicker}
            data={locationOptions}
            style={{ width: 300 }}
            disabled={isFetchingPackageItems || fp.size(packageItems) > 0}
            errorMessage={formError.locationId}
          />
        </FormGroup>
        <FormGroup>
          <ControlLabel>Items *</ControlLabel>
          {!allowEditPackageItems ? (
            <Message
              showIcon
              type="info"
              description="Cannot edit items: Package has active reservation order items. "
              style={{ marginBottom: 10, width: 400, display: 'inline-block' }}
            />
          ) : (
            <div style={{ width: 400, display: 'inline-block' }}>
              <SelectPicker
                key="packageItem"
                placeholder="Select Item"
                onChange={this.handlePackageItemChange}
                value={fp.getOr(null, 'item.id', packageItemValue)}
                data={nonExistingItemOptions}
                disabled={disabledInput}
                style={{ width: 120, marginRight: 10 }}
              />
              <SelectPicker
                key="packageZone"
                placeholder="Select Zone"
                onChange={this.handlePackageItemZoneChange}
                value={fp.getOr(null, 'zone.id', packageItemValue)}
                data={this.locationZonesOptions}
                disabled={disabledInput}
                style={{ width: 120, marginRight: 10 }}
              />
              <QuantityInput
                onChange={this.handlePackageItemQtyChange}
                min={1}
                value={packageItemValue.quantity}
                disabled={disabledInput}
                placeholder="Qty"
              />
              {!isFetchingPackageItems && (
                <IconButton
                  icon={<Icon icon="plus" />}
                  onClick={this.handleAddPackageItem}
                  disabled={disabledAddBtn}
                />
              )}
            </div>
          )}
        </FormGroup>
        <FormGroup>
          {isFetchingPackageItems ? (
            <Loader
              style={{ marginLeft: 180 }}
              content="Fetching package items..."
            />
          ) : (
            <PackageItemsList
              allowEdit={allowEditPackageItems}
              packageItems={packageItems}
              onIncrementQty={this.handleIncrementPackageItem}
              onDecrementQty={this.handleDecrementPackageItem}
              onUndoDelete={this.handleUndoDeletePackageItem}
              onDelete={this.handleDeletePackageItem}
            />
          )}
        </FormGroup>
      </Fragment>
    );
  }

  render() {
    const { packageItems } = this.props;
    const { formValue, formError, uploadProgress, uploadFile } = this.state;
    const { isPackage } = this;
    const isSaving = this.savingInProgress;
    const disableSaveBtn =
      isSaving ||
      !fp.isEmpty(formError) ||
      (isPackage &&
        (fp.isEmpty(packageItems) || fp.every('deleted', packageItems)));
    return (
      <Form
        style={{ maxWidth: 600 }}
        layout="horizontal"
        model={createModelSchema(formValue, isPackage)}
        ref={this.formRef}
        formValue={formValue}
        onChange={this.handleFormChange}
        onCheck={this.handleFormCheck}
      >
        <FormGroup>
          <ControlLabel>Photo</ControlLabel>
          {formValue.photo || uploadFile ? (
            this.renderPhoto()
          ) : (
            <DragDrop
              width="300px"
              height="130px"
              note={fp.get('opts.note', this.uppyUpload)}
              uppy={this.uppyUpload}
            />
          )}
          {uploadProgress && this.renderUploadProgress()}
        </FormGroup>
        <FormGroup>
          <ControlLabel>Name *</ControlLabel>
          <FormControl
            name="name"
            errorMessage={formError.name}
            placeholder="Item Name"
          />
        </FormGroup>
        <FormGroup>
          <ControlLabel>Description</ControlLabel>
          <FormControl
            name="description"
            rows={3}
            componentClass="textarea"
            errorMessage={formError.description}
          />
        </FormGroup>
        {isPackage && this.renderPackageItemsFormControls()}
        <FormGroup>
          <ControlLabel>Price</ControlLabel>
          <FormControl
            name="price"
            accepter={InputNumber}
            min={0}
            errorMessage={formError.price}
            readOnly={isPackage}
            placeholder="0.00"
          />
          {isPackage && <HelpBlock>Read only (calculated value)</HelpBlock>}
        </FormGroup>
        <FormGroup>
          <ControlLabel>
            <small>(lb) </small>
            Weight
          </ControlLabel>
          <FormControl
            name="weight"
            accepter={InputNumber}
            min={0}
            errorMessage={formError.weight}
            readOnly={isPackage}
            placeholder="0"
          />
          {isPackage && <HelpBlock>Read only (calculated value)</HelpBlock>}
        </FormGroup>
        <FormGroup>
          <ControlLabel>Points</ControlLabel>
          <FormControl
            name="points"
            accepter={InputNumber}
            min={0}
            errorMessage={formError.points}
            readOnly={isPackage}
            placeholder="0"
          />
          {isPackage && <HelpBlock>Read only (calculated value)</HelpBlock>}
        </FormGroup>
        {!isPackage && (
          <FormGroup>
            <ControlLabel>Min Count</ControlLabel>
            <FormControl
              name="minCount"
              accepter={InputNumber}
              min={0}
              errorMessage={formError.minCount}
              placeholder="0"
              onChange={this.handleMinCountOnChange}
            />
          </FormGroup>
        )}
        {!isPackage && (
          <FormGroup>
            <ControlLabel>Max Count</ControlLabel>
            <FormControl
              name="maxCount"
              accepter={InputNumber}
              min={0}
              errorMessage={formError.maxCount}
              placeholder="0"
            />
          </FormGroup>
        )}
        {!isPackage && (
          <FormGroup>
            <ControlLabel>Traffic-Light Nutrition Indicator</ControlLabel>
            <FormControl
              name="stoplight"
              accepter={SelectPicker}
              style={{ width: 300, marginTop: 10 }}
              data={HealthLevelOptions}
            />
          </FormGroup>
        )}
        <FormGroup>
          <ControlLabel>Tags</ControlLabel>
          <FormControl
            name="tags"
            accepter={TagPicker}
            data={this.customTagOption}
            style={{ width: 300 }}
            menuStyle={{ width: 300 }}
            errorMessage={formError.tags}
          />
        </FormGroup>

        <ButtonToolbar
          style={{ textAlign: 'right', marginTop: 30, marginRight: 110 }}
        >
          <Button
            onClick={this.handleReset}
            appearance="default"
            disabled={isSaving}
          >
            Reset
          </Button>
          <Button
            style={{ marginRight: 10 }}
            onClick={this.handleSubmit}
            appearance="primary"
            disabled={disableSaveBtn}
          >
            <Icon icon="save" /> {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </ButtonToolbar>
      </Form>
    );
  }
}
